/**
 * The story editor's graph-tab undo / redo shortcuts (spec 010), with the
 * scene editor's guards (spec 019): nothing while focus is in a text field,
 * select or contenteditable (the browser's own text undo applies there),
 * while a modal is open, or while the story preview runner plays.
 *
 * Cmd/Ctrl+Z → `storyStore.undo`; Cmd/Ctrl+Shift+Z or Ctrl+Y → `redo`.
 * Pass `enabled = false` on a scene tab, whose keys act on the scene (018).
 */

import { useEffect } from 'react';
import { useStoryStore } from '../stores/storyStore';
import { isEditingTarget, isModalOpen } from './useSceneShortcuts';

export function useStoryShortcuts(enabled = true): void {
  useEffect(() => {
    if (!enabled) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isEditingTarget(e.target) || isModalOpen()) return;
      const st = useStoryStore.getState();
      if (st.previewRunnerActive) return;

      const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const cmdKey = isMac ? e.metaKey : e.ctrlKey;
      const key = e.key.toLowerCase();
      if (cmdKey && key === 'z' && !e.shiftKey) {
        e.preventDefault();
        void st.undo();
      } else if ((cmdKey && key === 'z' && e.shiftKey) || (e.ctrlKey && key === 'y')) {
        e.preventDefault();
        void st.redo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enabled]);
}

export default useStoryShortcuts;
